import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import API from '../../api/axiosInstance';
import { configurationsApi } from '../../api/configurations';
import { BED_TYPES } from '../../constants/roomConfig';
import { BsPersonFill, BsSliders, BsPencil, BsCheckLg, BsXLg } from 'react-icons/bs';
import ConfigurationForm from './ConfigurationForm';
import DeleteConfirmModal from './DeleteConfirmModal';

export default function ConfigurationList({ room, onClose }) {
  const [configs, setConfigs]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [units, setUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(true);
  const [unitError, setUnitError] = useState('');
  const [pendingUnitIds, setPendingUnitIds] = useState(() => new Set());
  const [editingUnitLabel, setEditingUnitLabel] = useState(null); // { unitId, value } | null
  const [savingLabel, setSavingLabel] = useState(false);

  const fetchConfigs = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await configurationsApi.list(room._id);
      setConfigs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError('Failed to load configurations');
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnits = async (silent = false) => {
    if (!silent) setUnitsLoading(true);
    try {
      const res = await configurationsApi.listUnits(room._id);
      setUnits(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (!silent) setUnits([]);
    } finally {
      if (!silent) setUnitsLoading(false);
    }
  };

  useEffect(() => { fetchConfigs(); fetchUnits(); }, [room._id]);

  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleSave = async (data) => {
    try {
      if (editing) {
        await configurationsApi.update(room._id, editing._id, data);
      } else {
        await configurationsApi.create(room._id, data);
      }
      setShowForm(false);
      setEditing(null);
      fetchConfigs();
      fetchUnits(true);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save configuration');
    }
  };

  const handleDelete = (config) => setDeleteTarget(config);

  const confirmDelete = async () => {
    try {
      await configurationsApi.remove(room._id, deleteTarget._id);
      setDeleteTarget(null);
      fetchConfigs();
      fetchUnits(true);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete configuration');
    }
  };

  const handleUnitConfigChange = async (unit, newConfigId) => {
    setUnitError('');
    setPendingUnitIds((prev) => new Set(prev).add(unit._id));
    try {
      await API.put(`/ems/room-units/${unit._id}`, { configuration_id: newConfigId || null });
      await fetchUnits(true);
    } catch (err) {
      setUnitError(err.response?.data?.message || 'Failed to update unit configuration');
      await fetchUnits(true); // revert the dropdown to its actual server-side state, no spinner flash
    } finally {
      setPendingUnitIds((prev) => {
        const next = new Set(prev);
        next.delete(unit._id);
        return next;
      });
    }
  };

  const handleUnitLabelSave = async (unit) => {
    const value = editingUnitLabel?.value?.trim();
    if (!value) return;
    setSavingLabel(true);
    setUnitError('');
    try {
      await API.put(`/ems/room-units/${unit._id}`, { room_label: value });
      setEditingUnitLabel(null);
      await fetchUnits(true);
    } catch (err) {
      setUnitError(err.response?.data?.message || 'Failed to update unit label');
    } finally {
      setSavingLabel(false);
    }
  };

  const openAdd    = () => {
    setEditing(null);
    setUnitError('');
    setShowForm(true);
  };
  const openEdit   = (config) => { setEditing(config); setUnitError(''); setShowForm(true); };
  const handleCancel = () => { setShowForm(false); setEditing(null); setUnitError(''); };

  const roomLabel = room.room_name || room.room_type || 'Room';

  const assignedCounts = units.reduce((acc, u) => {
    const id = u.configuration_id?._id;
    if (id) acc[id] = (acc[id] || 0) + 1;
    return acc;
  }, {});

  return (
    ReactDOM.createPortal(
    <>
      <div className="hotel-modal-overlay" style={{ zIndex: 1050 }} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="config-modal-title"
        style={{
          position: 'fixed', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'white', width: '800px', maxWidth: '90vw',
          borderRadius: '10px', zIndex: 1051,
          boxShadow: '0 5px 20px rgba(0,0,0,0.3)',
          maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        <div style={{ padding: '16px 48px 0 20px', marginBottom: 15, position: 'relative' }}>
          <h5 id="config-modal-title" style={{ margin: 0, fontWeight: 600 }}>Configurations — {roomLabel}</h5>
          <button
            type="button"
            className="hotel-close-button"
            aria-label="Close"
            onClick={onClose}
            style={{ position: 'absolute', top: 8, right: 12, lineHeight: 1, padding: '4px 6px' }}
          >×</button>
        </div>

        <div style={{ padding: '0 20px 20px' }}>
            {error && <div className="alert alert-danger">{error}</div>}

            {showForm ? (
              <ConfigurationForm
                initial={editing || undefined}
                onSave={handleSave}
                onCancel={handleCancel}
              />
            ) : (
              <>
                {configs.length > 0 && (
                  <div className="d-flex justify-content-end mb-3">
                    <button
                      className="btn btn-sm"
                      style={{ background: '#D9A128', color: '#fff', whiteSpace: 'nowrap' }}
                      onClick={openAdd}
                    >
                      + Add Configuration
                    </button>
                  </div>
                )}

                {loading && (
                  <div style={{ textAlign: 'center', padding: '60px 20px', borderTop: '1px solid #f0ece3' }}>
                    <div className="spinner-border text-warning" role="status" style={{ width: 32, height: 32 }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}

                {!loading && configs.length === 0 && (
                  <div style={{
                    textAlign: 'center', padding: '40px 20px',
                    borderTop: '1px solid #f0ece3',
                  }}>
                    <div style={{ marginBottom: 12, color: '#D9A128' }}><BsSliders size={40} /></div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: '#1a1a1a', marginBottom: 6 }}>
                      No configurations yet
                    </div>
                    <div style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>
                      Add a configuration to define bed setups and pricing for this room.
                    </div>
                    <button
                      className="btn btn-sm"
                      style={{ background: '#D9A128', color: '#fff', padding: '6px 18px' }}
                      onClick={openAdd}
                    >
                      + Add Configuration
                    </button>
                  </div>
                )}

                {configs.map((cfg) => (
                  <div
                    key={cfg._id}
                    className="hotel-card mb-3"
                    style={{ border: '1.5px solid #D9A128' }}
                  >
                    <div className="d-flex align-items-center flex-wrap gap-2">
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#1a1a1a' }}>
                        {cfg.name}
                      </span>
                      <span style={{
                        background: '#1e2235', color: '#fff',
                        borderRadius: 12, padding: '3px 12px',
                        display: 'inline-flex', alignItems: 'baseline', gap: 3,
                        boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                      }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#D9A128' }}>${Number(cfg.price_per_night).toLocaleString()}</span>
                        <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.7 }}>/ night</span>
                      </span>
                      <span style={{
                        background: '#eeeeee', color: '#555',
                        borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 600,
                      }}>
                        {assignedCounts[cfg._id] || 0} rooms assigned
                      </span>
                      {(() => {
                        const total = cfg.beds.reduce((sum, b) => {
                          const pax = BED_TYPES.find(bt => bt.type === b.type && bt.region === b.region)?.pax ?? 0;
                          return sum + b.count * pax;
                        }, 0);
                        const totalAll = total * (assignedCounts[cfg._id] || 0);
                        return total > 0 ? (
                          <span style={{
                            background: '#eef4ff', color: '#2255aa',
                            borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 600,
                          }}>
                            {total} per room · {totalAll} total
                          </span>
                        ) : null;
                      })()}
                      <div className="d-flex gap-2" style={{ marginLeft: 'auto' }}>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => openEdit(cfg)}>
                          Edit
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(cfg)}>
                          Delete
                        </button>
                      </div>
                    </div>

                    <div style={{ borderTop: '1px solid #f0ece3', margin: '12px 0' }} />

                    <div className="d-flex align-items-center flex-wrap gap-1">
                      <span style={{
                        fontSize: 11, textTransform: 'uppercase', color: '#888',
                        letterSpacing: '0.6px', fontWeight: 600, marginRight: 4,
                      }}>
                        Beds
                      </span>
                      {cfg.beds.map((b, i) => {
                        const pax = BED_TYPES.find(bt => bt.type === b.type && bt.region === b.region)?.pax;
                        return (
                          <span key={i} style={{
                            background: '#FDEAB0', color: '#7a5500',
                            borderRadius: 6, fontSize: 12, fontWeight: 600,
                            border: '1px solid #f0d080',
                            display: 'inline-flex', alignItems: 'center', overflow: 'hidden',
                          }}>
                            <span style={{ padding: '4px 9px' }}>
                              <strong style={{ color: '#5a3d00' }}>{b.count}</strong>× {b.type}
                            </span>
                            {pax != null && (
                              <>
                                <span style={{ width: 1, alignSelf: 'stretch', background: '#f0d080' }} />
                                <span style={{ padding: '4px 9px', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  {pax} <BsPersonFill size={13} /> / bed
                                </span>
                              </>
                            )}
                          </span>
                        );
                      })}
                    </div>

                    {cfg.features.length > 0 && (
                      <div className="d-flex align-items-center flex-wrap gap-1 mt-1">
                        <span style={{
                          fontSize: 11, textTransform: 'uppercase', color: '#888',
                          letterSpacing: '0.6px', fontWeight: 600, marginRight: 4,
                        }}>
                          Features
                        </span>
                        {cfg.features.map((f, i) => (
                          <span key={i} style={{
                            background: '#f0f0f0', color: '#444',
                            borderRadius: 6, padding: '3px 9px', fontSize: 12, fontWeight: 500,
                            border: '1px solid #d8d8d8',
                          }}>
                            {f}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                <div style={{ borderTop: '1px solid #f0ece3', marginTop: 20, paddingTop: 16 }}>
                  <h6 style={{ fontSize: 12, textTransform: 'uppercase', color: '#888', letterSpacing: '0.4px', fontWeight: 600, marginBottom: 10 }}>
                    Units
                  </h6>
                  {unitError && (
                    <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>
                      {unitError}
                    </div>
                  )}
                  {unitsLoading ? (
                    <div style={{ textAlign: 'center', padding: 20 }}>
                      <div className="spinner-border spinner-border-sm text-warning" role="status" />
                    </div>
                  ) : units.length === 0 ? (
                    <p style={{ fontSize: 13, color: '#888', margin: 0 }}>No units for this room.</p>
                  ) : (
                    units.map((unit) => {
                      const isPending = pendingUnitIds.has(unit._id);
                      return (
                        <div
                          key={unit._id}
                          className="d-flex align-items-center justify-content-between"
                          style={{ padding: '8px 0', borderBottom: '1px solid #f5f5f5' }}
                        >
                          {editingUnitLabel?.unitId === unit._id ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <input
                                type="text"
                                autoFocus
                                value={editingUnitLabel.value}
                                onChange={(e) => setEditingUnitLabel({ ...editingUnitLabel, value: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleUnitLabelSave(unit);
                                  if (e.key === 'Escape') setEditingUnitLabel(null);
                                }}
                                disabled={savingLabel}
                                style={{ width: 70, fontSize: 13, padding: '2px 6px', border: '1px solid #ccc', borderRadius: 4 }}
                              />
                              <BsCheckLg
                                size={14}
                                style={{ cursor: savingLabel ? 'default' : 'pointer', color: '#198754', opacity: savingLabel ? 0.5 : 1 }}
                                onClick={() => !savingLabel && handleUnitLabelSave(unit)}
                              />
                              <BsXLg
                                size={12}
                                style={{ cursor: savingLabel ? 'default' : 'pointer', color: '#dc3545', opacity: savingLabel ? 0.5 : 1 }}
                                onClick={() => !savingLabel && setEditingUnitLabel(null)}
                              />
                            </div>
                          ) : (
                            <span style={{ fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              {unit.room_label}
                              <BsPencil
                                size={12}
                                style={{ cursor: 'pointer', color: '#999' }}
                                title="Edit room label"
                                onClick={() => setEditingUnitLabel({ unitId: unit._id, value: unit.room_label })}
                              />
                            </span>
                          )}
                          {isPending ? (
                            <div style={{ maxWidth: 240, width: 240, display: 'flex', justifyContent: 'center' }}>
                              <div className="spinner-border spinner-border-sm text-warning" role="status">
                                <span className="visually-hidden">Saving…</span>
                              </div>
                            </div>
                          ) : (
                            <select
                              className="form-select form-select-sm"
                              style={{ maxWidth: 240 }}
                              value={unit.configuration_id?._id || ''}
                              onChange={(e) => handleUnitConfigChange(unit, e.target.value)}
                            >
                              <option value="">Unconfigured</option>
                              {configs.map((c) => (
                                <option key={c._id} value={c._id}>
                                  {c.name}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}
        </div>
      </div>
      {deleteTarget && (
        <DeleteConfirmModal
          name={deleteTarget.name}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </>
    , document.body)
  );
}
